// src/pages/SAP/Components/SapCard/sapcard.jsx
import React, { useState, useEffect } from 'react';
import './sapcard.css';
import { NumericFormat } from 'react-number-format';
import { 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaHandshake, 
  FaThumbsUp, 
  FaThumbsDown,
  FaSearchPlus,
  FaTimes
} from 'react-icons/fa';

function formatLeadingZero(value) {
    const num = parseInt(value, 10);
    return num < 10 ? `00${num}` : num < 100 ? `0${num}` : `${num}`;
}

function formatNumber(amount) {
    try {
        const num = Number(amount) || 0;
        return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (e) {
        return "0,00";
    }
}

const PriceInfo = ({ label, value }) => (
    <div className="cardsap-price-badge">
        <span className="price-label">{label}:</span>
        <span className="price-value">R$ {formatNumber(value)}</span>
    </div>
);

const MotivoBadge = ({ motivo, precoConcorrente }) => {
    if (!motivo) return null;
    const isConcorrencia = motivo.toUpperCase() === 'CONCORRÊNCIA';
    const bgClass = isConcorrencia ? 'bg-warning text-dark' : 'bg-custom-red text-white';
    const text = isConcorrencia ? `${motivo} - R$ ${formatNumber(precoConcorrente)}` : motivo;

    return <div className={`cardsap-motivo-tag ${bgClass}`}>{text}</div>;
};

// Modal Interno para Ampliar Foto
function FotoModal({ show, onClose, imgSrc, altText }) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (show) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [show, onClose]);

    if (!show) return null;

    return (
        <div className="sap-foto-modal-overlay" onClick={onClose}>
            <div className="sap-foto-modal-content" onClick={(e) => e.stopPropagation()}>
                <button 
                    type="button" 
                    className="sap-foto-modal-close" 
                    onClick={onClose} 
                    title="Fechar (Esc)"
                >
                    <FaTimes />
                </button>
                <img src={imgSrc} alt={altText} className="sap-foto-modal-img" />
                <div className="sap-foto-modal-caption">{altText}</div>
            </div>
        </div>
    );
}

function SapCard({
    sap_loja,
    sap_data,
    sap_nomecomprador,
    sap_codigo,
    sap_descricao,
    nome,
    funcao,
    sap_custo,
    sap_preco,
    sap_precopromo,
    sap_precoapp,
    sap_precosolicitado,
    sap_motivo,
    sap_precoconcorrente,
    sap_observacao,
    sap_sequencia,
    ean,
    clickAprovar,
    clickNegar,
    onSelecionar,
    selecionado,
    onprecoEditadoChange
}) {
    const [precoEditado, setPrecoEditado] = useState(sap_precosolicitado);
    const [showFotoModal, setShowFotoModal] = useState(false);
    
    const defaultImg = 'https://multilit.com.br/wp-content/uploads/2020/03/Produto-sem-foto.png';
    const initialImgSrc = ean ? `https://cdn-cosmos.bluesoft.com.br/products/${ean}` : defaultImg;
    const [imgSrc, setImgSrc] = useState(initialImgSrc);

    useEffect(() => {
        setPrecoEditado(sap_precosolicitado);
    }, [sap_precosolicitado]);

    const handleChange = (e) => {
        onSelecionar(e.target.checked, {
            loja: sap_loja,
            codigo: sap_codigo,
            datasol: sap_data,
            sequencia: sap_sequencia,
            preco: precoEditado,
            descricao: sap_descricao
        });
    };

    function handleAprovar() {
        clickAprovar(sap_data, sap_loja, sap_sequencia, sap_codigo, sap_descricao, precoEditado);
    }

    function handleNegar() {
        clickNegar(sap_data, sap_loja, sap_sequencia, sap_codigo, sap_descricao, precoEditado);
    }

    return (
        <>
            <div className={`sap-card-modern ${selecionado ? 'card-selecionado' : ''}`}>
                {/* SELEÇÃO CHECKBOX */}
                <div className="cardsap-header-select">
                    <div className="form-check m-0 d-flex align-items-center gap-2">
                        <input
                            type="checkbox"
                            className="form-check-input cursor-pointer"
                            checked={selecionado}
                            onChange={handleChange}
                            id={`select-${sap_loja}-${sap_codigo}-${sap_sequencia}`}
                        />
                        <label className="form-check-label small fw-bold text-secondary cursor-pointer" htmlFor={`select-${sap_loja}-${sap_codigo}-${sap_sequencia}`}>
                            Selecionar
                        </label>
                    </div>
                </div>

                {/* CABEÇALHO COM LOJA, DATA E COMPRADOR */}
                <div className="cardsap-header-info">
                    <div className="d-flex justify-content-between align-items-center small text-muted mb-1">
                        <span className="fw-bold text-dark d-flex align-items-center gap-1">
                            <FaMapMarkerAlt className="text-custom-red" /> Loja {formatLeadingZero(sap_loja)}
                        </span>
                        <span className="d-flex align-items-center gap-1 text-danger fw-semibold">
                            <FaCalendarAlt /> {sap_data}
                        </span>
                    </div>

                    {sap_nomecomprador && (
                        <div className="small text-secondary d-flex align-items-center gap-1 mb-2 cardsap-buyer">
                            <FaHandshake /> {sap_nomecomprador}
                        </div>
                    )}

                    <h6 className="cardsap-descricao-title mb-2" title={`${sap_codigo} - ${sap_descricao}`}>
                        <b>{sap_codigo}</b> - {sap_descricao}
                    </h6>
                </div>

                {/* FOTO DO PRODUTO (CLICÁVEL PARA AMPLIAR) */}
                <div 
                    className="cardsap-img-container position-relative cursor-pointer"
                    onClick={() => setShowFotoModal(true)}
                    title="Clique para ampliar a foto"
                >
                    <img
                        src={imgSrc}
                        alt={sap_descricao}
                        onError={() => setImgSrc(defaultImg)}
                    />
                    <div className="cardsap-img-zoom-badge">
                        <FaSearchPlus />
                    </div>
                </div>

                {/* SOLICITANTE */}
                <div className="cardsap-solicitante text-center">
                    <small className="text-muted d-block text-truncate">
                        <b>Solicitante:</b> {nome} {funcao ? `- ${funcao}` : ''}
                    </small>
                </div>

                {/* GRID DE PREÇOS ATUAIS */}
                <div className="cardsap-prices-grid my-2">
                    <PriceInfo label="Custo" value={sap_custo} />
                    <PriceInfo label="Preço" value={sap_preco} />
                    <PriceInfo label="Promo" value={sap_precopromo} />
                    <PriceInfo label="App" value={sap_precoapp} />
                </div>

                {/* CAMPO DE PREÇO SOLICITADO (EDITÁVEL) */}
                <div className="cardsap-solicitado-box text-center my-2">
                    <label className="x-small fw-bold text-uppercase text-muted d-block mb-1">Preço Solicitado:</label>
                    <NumericFormat
                        className="form-control form-control-sm preco-input text-center fw-bold"
                        value={precoEditado}
                        onValueChange={(values) => {
                            const val = values.value;
                            setPrecoEditado(val);
                            if (onprecoEditadoChange) {
                                onprecoEditadoChange(sap_loja, sap_codigo, sap_sequencia, parseFloat(val) || 0);
                            }
                        }}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                    />
                </div>

                {/* MOTIVO DA SOLICITAÇÃO */}
                <MotivoBadge motivo={sap_motivo} precoConcorrente={sap_precoconcorrente} />

                {/* OBSERVAÇÃO */}
                {sap_observacao && (
                    <div className="cardsap-observacao-box mt-2">
                        <small className="text-secondary d-block text-center">{sap_observacao}</small>
                    </div>
                )}

                {/* BOTÕES DE AÇÃO */}
                <div className="cardsap-actions mt-3">
                    <button 
                        type="button" 
                        onClick={handleNegar} 
                        className="btn btn-danger btn-sm cardsap-btn-action"
                        title="Reprovar Solicitação"
                    >
                        <FaThumbsDown /> Reprovar
                    </button>
                    <button 
                        type="button" 
                        onClick={handleAprovar} 
                        className="btn btn-success btn-sm cardsap-btn-action"
                        title="Aprovar Solicitação"
                    >
                        <FaThumbsUp /> Aprovar
                    </button>
                </div>
            </div>

            {/* MODAL DE AMPLIAÇÃO DA FOTO */}
            <FotoModal
                show={showFotoModal}
                onClose={() => setShowFotoModal(false)}
                imgSrc={imgSrc}
                altText={`${sap_codigo} - ${sap_descricao}`}
            />
        </>
    );
}

export default SapCard;